// # QBank Statistics Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis 
} from 'recharts';
import './stats1_qbnk.css';
import { 
  FaChartBar, 
  FaChartPie,
  FaChartLine,
  FaStar,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaPercentage,
  FaQuestionCircle
} from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import cacheManager from '../../../auth/cache_manager';

// # Define chart colors
const CHART_COLORS = {
  primary: '#3498db',
  success: '#2ecc71',
  danger: '#e74c3c',
  warning: '#f1c40f',
  info: '#9b59b6',
  chartColors: ['#3498db', '#2ecc71', '#e74c3c', '#f1c40f', '#9b59b6']
};

function QBankStats({ isAdmin, isTutor, isMentorView, menteeId, isDarkMode }) {
  const { user } = useAuthStore();
  const [targetUserId, setTargetUserId] = useState(null);
  const [qbankData, setQbankData] = useState({
    uw1: {},
    amboss: {},
    nbme: {}
  });
  const [selectedChart, setSelectedChart] = useState('performance');

  // # Get target user ID
  useEffect(() => {
    const userId = menteeId || sessionStorage.getItem('currentViewingMenteeId') || user?.uid;
    setTargetUserId(userId);
  }, [user?.uid, menteeId]);

  // # Load qbank data
  useEffect(() => {
    const loadQBankData = async () => {
      if (!targetUserId) return;

      try {
        // # Load Step 1 data
        const s1Doc = await getDoc(doc(db, 's1_mentee', targetUserId));
        const s1Data = s1Doc.data() || {};
        
        setQbankData({
          uw1: s1Data.uworld || {},
          amboss: s1Data.amboss || {},
          nbme: s1Data.nbme || {}
        });
      } catch (error) {
        console.error('Error loading qbank data:', error);
      }
    };

    loadQBankData();
  }, [targetUserId]);

  // # Calculate performance stats
  const calculateStats = () => {
    const stats = {
      uw1: {
        totalQuestions: 0,
        correctQuestions: 0,
        incorrectQuestions: 0,
        averagePerformance: 0,
        topChapters: [],
        bottomChapters: [],
        weakPoints: []
      },
      amboss: {
        totalQuestions: 0,
        correctQuestions: 0,
        incorrectQuestions: 0,
        averagePerformance: 0,
        topChapters: [],
        bottomChapters: [],
        weakPoints: []
      },
      nbme: {
        totalQuestions: 0,
        correctQuestions: 0,
        incorrectQuestions: 0,
        averagePerformance: 0,
        topExams: [],
        bottomExams: [],
        weakPoints: []
      }
    };

    // # Collect weak points from completed chapters/exams
    Object.entries(qbankData.uw1).forEach(([chapter, data]) => {
      if (data.status === 'Completed' && data.weaknesses?.length > 0) {
        stats.uw1.weakPoints.push({
          chapter,
          weaknesses: data.weaknesses
        });
      }
    });

    Object.entries(qbankData.amboss).forEach(([chapter, data]) => {
      if (data.status === 'Completed' && data.weaknesses?.length > 0) {
        stats.amboss.weakPoints.push({
          chapter,
          weaknesses: data.weaknesses
        });
      }
    });

    Object.entries(qbankData.nbme).forEach(([exam, data]) => {
      if (data.status === 'Completed' && data.weaknesses?.length > 0) {
        stats.nbme.weakPoints.push({
          exam,
          weaknesses: data.weaknesses
        });
      }
    });

    // # Calculate UWorld Step 1 stats
    Object.entries(qbankData.uw1).forEach(([chapter, data]) => {
      if (chapter !== 'Random') {
        stats.uw1.totalQuestions += (data.correct || 0) + (data.incorrect || 0);
        stats.uw1.correctQuestions += data.correct || 0;
        stats.uw1.incorrectQuestions += data.incorrect || 0;
      }
    });

    // # Calculate AMBOSS stats
    Object.entries(qbankData.amboss).forEach(([chapter, data]) => {
      if (chapter !== 'Random') {
        stats.amboss.totalQuestions += (data.correct || 0) + (data.incorrect || 0);
        stats.amboss.correctQuestions += data.correct || 0;
        stats.amboss.incorrectQuestions += data.incorrect || 0;
      }
    });

    // # Calculate NBME stats
    Object.entries(qbankData.nbme).forEach(([exam, data]) => {
      stats.nbme.totalQuestions += (data.correct || 0) + (data.incorrect || 0);
      stats.nbme.correctQuestions += data.correct || 0;
      stats.nbme.incorrectQuestions += data.incorrect || 0;
    });

    // # Calculate averages
    stats.uw1.averagePerformance = stats.uw1.totalQuestions ? 
      (stats.uw1.correctQuestions / stats.uw1.totalQuestions * 100).toFixed(1) : 0;
    stats.amboss.averagePerformance = stats.amboss.totalQuestions ? 
      (stats.amboss.correctQuestions / stats.amboss.totalQuestions * 100).toFixed(1) : 0;
    stats.nbme.averagePerformance = stats.nbme.totalQuestions ? 
      (stats.nbme.correctQuestions / stats.nbme.totalQuestions * 100).toFixed(1) : 0;

    // # Get top and bottom chapters for UWorld Step 1 (only completed)
    const uw1CompletedChapters = Object.entries(qbankData.uw1)
      .filter(([chapter, data]) => 
        chapter !== 'Random' && 
        data.status === 'Completed' &&
        data.percentage > 0
      )
      .map(([chapter, data]) => ({
        name: chapter,
        percentage: Number(data.percentage) || 0
      }))
      .sort((a, b) => b.percentage - a.percentage);

    // # Take unique top 5 and bottom 5 based on percentage
    stats.uw1.topChapters = uw1CompletedChapters.slice(0, 5);
    stats.uw1.bottomChapters = [...uw1CompletedChapters]
      .sort((a, b) => a.percentage - b.percentage)
      .slice(0, 5);

    // # Get top and bottom chapters for AMBOSS (only completed)
    const ambossCompletedChapters = Object.entries(qbankData.amboss)
      .filter(([chapter, data]) => 
        chapter !== 'Random' && 
        data.status === 'Completed' &&
        data.percentage > 0
      )
      .map(([chapter, data]) => ({
        name: chapter,
        percentage: Number(data.percentage) || 0
      }))
      .sort((a, b) => b.percentage - a.percentage);

    // # Take unique top 5 and bottom 5 based on percentage
    stats.amboss.topChapters = ambossCompletedChapters.slice(0, 5);
    stats.amboss.bottomChapters = [...ambossCompletedChapters]
      .sort((a, b) => a.percentage - b.percentage)
      .slice(0, 5);

    // # Get top and bottom NBME exams (only completed)
    const nbmeCompletedExams = Object.entries(qbankData.nbme)
      .filter(([exam, data]) => 
        data.status === 'Completed' &&
        data.percentage > 0
      )
      .map(([exam, data]) => ({
        name: exam,
        percentage: Number(data.percentage) || 0
      }))
      .sort((a, b) => b.percentage - a.percentage);

    // # Take unique top 3 and bottom 3 based on percentage
    stats.nbme.topExams = nbmeCompletedExams.slice(0, 3);
    stats.nbme.bottomExams = [...nbmeCompletedExams]
      .sort((a, b) => a.percentage - b.percentage)
      .slice(0, 3);

    return stats;
  };

  const stats = calculateStats();

  // # Render performance chart
  const renderPerformanceChart = () => {
    const data = [
      {
        name: 'UWorld Step 1',
        correct: stats.uw1.correctQuestions,
        incorrect: stats.uw1.incorrectQuestions,
        percentage: Number(stats.uw1.averagePerformance)
      },
      {
        name: 'AMBOSS',
        correct: stats.amboss.correctQuestions,
        incorrect: stats.amboss.incorrectQuestions,
        percentage: Number(stats.amboss.averagePerformance)
      },
      {
        name: 'NBME',
        correct: stats.nbme.correctQuestions,
        incorrect: stats.nbme.incorrectQuestions,
        percentage: Number(stats.nbme.averagePerformance)
      }
    ];

    return (
      <div className="stats1_qbnk_chart">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="correct" name="Correct" fill={CHART_COLORS.success} />
            <Bar dataKey="incorrect" name="Incorrect" fill={CHART_COLORS.danger} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // # Render top performers chart
  const renderTopPerformersChart = () => {
    const data = [
      ...stats.uw1.topChapters.map(chapter => ({
        ...chapter,
        category: 'UWorld Step 1'
      })),
      ...stats.amboss.topChapters.map(chapter => ({
        ...chapter,
        category: 'AMBOSS'
      })),
      ...stats.nbme.topExams.map(exam => ({
        ...exam,
        category: 'NBME'
      }))
    ];

    return (
      <div className="stats1_qbnk_chart">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="percentage" name="Performance %" fill={CHART_COLORS.primary} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // # Render bottom performers chart
  const renderBottomPerformersChart = () => {
    const data = [
      ...stats.uw1.bottomChapters.map(chapter => ({
        ...chapter,
        category: 'UWorld Step 1'
      })),
      ...stats.amboss.bottomChapters.map(chapter => ({
        ...chapter,
        category: 'AMBOSS'
      })),
      ...stats.nbme.bottomExams.map(exam => ({
        ...exam,
        category: 'NBME'
      }))
    ];

    return (
      <div className="stats1_qbnk_chart">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="percentage" name="Performance %" fill={CHART_COLORS.warning} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className={`stats1_qbnk_container ${isDarkMode ? 'stats1_qbnk_dark' : ''}`}>
      <div className="stats1_qbnk_content">
        <h1 className="stats1_qbnk_title">QBank Performance Analytics</h1>

        {/* Overall Stats Cards */}
        <div className="stats1_qbnk_stats_grid">
          <div className="stats1_qbnk_stat_card">
            <div className="stats1_qbnk_stat_icon">
              <FaQuestionCircle />
            </div>
            <div className="stats1_qbnk_stat_content">
              <h3>Total Questions</h3>
              <p>{stats.uw1.totalQuestions + stats.amboss.totalQuestions + stats.nbme.totalQuestions}</p>
            </div>
          </div>

          <div className="stats1_qbnk_stat_card">
            <div className="stats1_qbnk_stat_icon success">
              <FaCheckCircle />
            </div>
            <div className="stats1_qbnk_stat_content">
              <h3>Total Correct</h3>
              <p>{stats.uw1.correctQuestions + stats.amboss.correctQuestions + stats.nbme.correctQuestions}</p>
            </div>
          </div>

          <div className="stats1_qbnk_stat_card">
            <div className="stats1_qbnk_stat_icon danger">
              <FaTimesCircle />
            </div>
            <div className="stats1_qbnk_stat_content">
              <h3>Total Incorrect</h3>
              <p>{stats.uw1.incorrectQuestions + stats.amboss.incorrectQuestions + stats.nbme.incorrectQuestions}</p>
            </div>
          </div>

          <div className="stats1_qbnk_stat_card">
            <div className="stats1_qbnk_stat_icon warning">
              <FaPercentage />
            </div>
            <div className="stats1_qbnk_stat_content">
              <h3>Overall Performance</h3>
              <p>
                {((
                  (stats.uw1.correctQuestions + stats.amboss.correctQuestions + stats.nbme.correctQuestions) /
                  (stats.uw1.totalQuestions + stats.amboss.totalQuestions + stats.nbme.totalQuestions) * 100
                ) || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="stats1_qbnk_charts">
          <div className="stats1_qbnk_chart">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={[
                {
                  name: 'UWorld Step 1',
                  correct: stats.uw1.correctQuestions,
                  incorrect: stats.uw1.incorrectQuestions,
                  percentage: Number(stats.uw1.averagePerformance)
                },
                {
                  name: 'AMBOSS',
                  correct: stats.amboss.correctQuestions,
                  incorrect: stats.amboss.incorrectQuestions,
                  percentage: Number(stats.amboss.averagePerformance)
                },
                {
                  name: 'NBME',
                  correct: stats.nbme.correctQuestions,
                  incorrect: stats.nbme.incorrectQuestions,
                  percentage: Number(stats.nbme.averagePerformance)
                }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="correct" name="Correct" fill={CHART_COLORS.success} />
                <Bar dataKey="incorrect" name="Incorrect" fill={CHART_COLORS.danger} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="stats1_qbnk_detailed_stats">
          {/* UWorld Step 1 Stats */}
          <div className="stats1_qbnk_detailed_section">
            <h2>UWorld Step 1</h2>
            <div className="stats1_qbnk_performance_list">
              <div className="stats1_qbnk_performance_section">
                <h3>Top 5 Chapters</h3>
                {stats.uw1.topChapters.length > 0 ? (
                  <ul>
                    {stats.uw1.topChapters.map((chapter, index) => (
                      <li key={index}>
                        <span>{chapter.name}</span>
                        <span>{chapter.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed chapters available</p>
                  </div>
                )}
              </div>
              <div className="stats1_qbnk_performance_section">
                <h3>Bottom 5 Chapters</h3>
                {stats.uw1.bottomChapters.length > 0 ? (
                  <ul>
                    {stats.uw1.bottomChapters.map((chapter, index) => (
                      <li key={index}>
                        <span>{chapter.name}</span>
                        <span>{chapter.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed chapters available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AMBOSS Stats */}
          <div className="stats1_qbnk_detailed_section">
            <h2>AMBOSS</h2>
            <div className="stats1_qbnk_performance_list">
              <div className="stats1_qbnk_performance_section">
                <h3>Top 5 Chapters</h3>
                {stats.amboss.topChapters.length > 0 ? (
                  <ul>
                    {stats.amboss.topChapters.map((chapter, index) => (
                      <li key={index}>
                        <span>{chapter.name}</span>
                        <span>{chapter.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed chapters available</p>
                  </div>
                )}
              </div>
              <div className="stats1_qbnk_performance_section">
                <h3>Bottom 5 Chapters</h3>
                {stats.amboss.bottomChapters.length > 0 ? (
                  <ul>
                    {stats.amboss.bottomChapters.map((chapter, index) => (
                      <li key={index}>
                        <span>{chapter.name}</span>
                        <span>{chapter.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed chapters available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* NBME Stats */}
          <div className="stats1_qbnk_detailed_section">
            <h2>NBME Performance</h2>
            <div className="stats1_qbnk_performance_list">
              <div className="stats1_qbnk_performance_section">
                <h3>Top 3 Exams</h3>
                {stats.nbme.topExams.length > 0 ? (
                  <ul>
                    {stats.nbme.topExams.map((exam, index) => (
                      <li key={index}>
                        <span>{exam.name}</span>
                        <span>{exam.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed exams available</p>
                  </div>
                )}
              </div>
              <div className="stats1_qbnk_performance_section">
                <h3>Bottom 3 Exams</h3>
                {stats.nbme.bottomExams.length > 0 ? (
                  <ul>
                    {stats.nbme.bottomExams.map((exam, index) => (
                      <li key={index}>
                        <span>{exam.name}</span>
                        <span>{exam.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="stats1_qbnk_no_data">
                    <FaExclamationTriangle />
                    <p>No completed exams available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Weak Points Section */}
        <div className="stats1_qbnk_weak_points">
          <h2 className="stats1_qbnk_section_title">Weak Points Analysis</h2>
          
          <div className="stats1_qbnk_weak_points_grid">
            {/* UWorld Weak Points */}
            <div className="stats1_qbnk_weak_points_card">
              <h3>UWorld Step 1 Weak Points</h3>
              {stats.uw1.weakPoints.length > 0 ? (
                <div className="stats1_qbnk_weak_points_list">
                  {stats.uw1.weakPoints.map((item, index) => (
                    <div key={index} className="stats1_qbnk_weak_points_item">
                      <h4>{item.chapter}</h4>
                      <ul>
                        {item.weaknesses.map((weakness, wIndex) => (
                          <li key={wIndex}>{weakness}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="stats1_qbnk_no_data">
                  <FaExclamationTriangle />
                  <p>No weak points recorded</p>
                </div>
              )}
            </div>

            {/* AMBOSS Weak Points */}
            <div className="stats1_qbnk_weak_points_card">
              <h3>AMBOSS Weak Points</h3>
              {stats.amboss.weakPoints.length > 0 ? (
                <div className="stats1_qbnk_weak_points_list">
                  {stats.amboss.weakPoints.map((item, index) => (
                    <div key={index} className="stats1_qbnk_weak_points_item">
                      <h4>{item.chapter}</h4>
                      <ul>
                        {item.weaknesses.map((weakness, wIndex) => (
                          <li key={wIndex}>{weakness}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="stats1_qbnk_no_data">
                  <FaExclamationTriangle />
                  <p>No weak points recorded</p>
                </div>
              )}
            </div>

            {/* NBME Weak Points */}
            <div className="stats1_qbnk_weak_points_card">
              <h3>NBME Weak Points</h3>
              {stats.nbme.weakPoints.length > 0 ? (
                <div className="stats1_qbnk_weak_points_list">
                  {stats.nbme.weakPoints.map((item, index) => (
                    <div key={index} className="stats1_qbnk_weak_points_item">
                      <h4>{item.exam}</h4>
                      <ul>
                        {item.weaknesses.map((weakness, wIndex) => (
                          <li key={wIndex}>{weakness}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="stats1_qbnk_no_data">
                  <FaExclamationTriangle />
                  <p>No weak points recorded</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default QBankStats;
